/**
 * Chart Renderer for PDF Export
 * 
 * Draws horizontal bar charts for all Market Data report types:
 * - Projected Patients (single bar by location/care setting)
 * - Shared Affiliation (stacked bar: affiliated vs non-affiliated)
 * - Payors (stacked bar: up to 3 payors + other)
 */

import { SpecialtyUtil } from 'doctivity-shared/utils';
import { 
  getLocationTypeColor, 
  LOCATION_TYPES_FOR_LEGEND,
  PDF_SETTINGS,
  getSettingsByType,
  getColorsByType,
  getLegendByType,
  PAGE_HEADER_STYLES,
  PAGE_FOOTER_STYLES,
} from './pdfStyles';

// Colors for Shared Affiliation chart
export const AFFILIATION_COLORS = {
  affiliated: [32, 131, 244],      // Blue - matches CSS rgba(32, 131, 244, 0.7)
  nonAffiliated: [255, 107, 107],  // Red - rgb(255, 107, 107)
};

// Default payor colors matching the on-screen chart (ColorUtil.COLORS)
export const DEFAULT_PAYOR_COLORS = [
  [30, 136, 229],   // Blue - Medicare
  [67, 160, 71],    // Green - Medicaid  
  [255, 167, 38],   // Orange - third payor
  [227, 227, 227],  // Gray (Other)
];

/**
 * Convert text to Title Case (capitalize first letter of each word)
 */
const toTitleCase = (str) => {
  if (!str) return str;
  return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Convert date range from "Oct 2024 - Sep 2025" to "10/1/24 - 9/30/25" format
 */
const formatDateRangeShort = (dateRange) => {
  if (!dateRange) return '';
  
  const months = {
    'Jan': 1, 'Feb': 2, 'Mar': 3, 'Apr': 4, 'May': 5, 'Jun': 6,
    'Jul': 7, 'Aug': 8, 'Sep': 9, 'Oct': 10, 'Nov': 11, 'Dec': 12
  };
  
  // Parse "Mon YYYY - Mon YYYY" format
  const parts = dateRange.split(' - ');
  if (parts.length !== 2) return dateRange;
  
  const formatDate = (dateStr, isEnd) => {
    const [month, year] = dateStr.trim().split(' ');
    const monthNum = months[month];
    if (!monthNum || !year) return dateStr;
    
    // For end date, use last day of month; for start date, use 1st
    const day = isEnd ? new Date(parseInt(year), monthNum, 0).getDate() : 1;
    const shortYear = year.slice(-2);
    return `${monthNum}/${day}/${shortYear}`;
  };
  
  return `${formatDate(parts[0], false)} - ${formatDate(parts[1], true)}`;
};

/**
 * Draws full-page gradient background with angled white section for logo
 * TOP = #18b095 (Teal), BOTTOM = #348cf5 (Blue)
 * 
 * @param {jsPDF} doc - The jsPDF document instance
 * @param {string} title - The main title text
 * @param {string} subtitle - Optional subtitle text (empty string for none)
 * @param {Array} filterPills - (not used yet)
 * @param {Object} options - Options including pdfType
 * @returns {number} - Y position for content
 */
export const drawPageHeader = (doc, title, subtitle = '', filterPills = [], options = {}) => {
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const { dateRange } = options;
  
  // Create gradient using canvas for smooth result
  const canvas = document.createElement('canvas');
  canvas.width = 1;  // Only need 1 pixel wide for vertical gradient
  canvas.height = Math.ceil(pageHeight);
  
  const ctx = canvas.getContext('2d');
  const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
  gradient.addColorStop(0, '#18b095');  // Teal at TOP
  gradient.addColorStop(1, '#348cf5');  // Blue at BOTTOM
  
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  // Convert to base64 and add to PDF
  const imgData = canvas.toDataURL('image/png');
  doc.addImage(imgData, 'PNG', 0, 0, pageWidth, pageHeight);
  
  // Draw white angled section across top for logo area
  // Quadrilateral: all white from top down to the angled line
  // Line goes from 10px down on left to angleEndY on right
  const { pdfType = 'printable' } = options;
  const anglePercent = pdfType === 'slideshow' ? 0.083 : 0.05;  // 8.3% for slideshow, 5% for printable
  const angleEndY = pageHeight * anglePercent;
  const angleStartY = 10;  // 10px down on the left
  
  doc.setFillColor(255, 255, 255);
  
  // Draw quadrilateral (trapezoid) - everything above the angled line is white
  // Use lines() to draw 4-sided shape
  const points = [
    [pageWidth, 0],                    // Move to top-right
    [0, angleEndY],                    // Down to right side point
    [-pageWidth, angleStartY - angleEndY],  // Across to left side point (0, 10)
    [0, -angleStartY],                 // Up to top-left corner
  ];
  doc.lines(points, 0, 0, [1, 1], 'F', true);
  
  // Add title in the top gradient area - WHITE text
  doc.setFont('Roboto', 'normal');
  doc.setFontSize(20);
  doc.setTextColor(255, 255, 255);  // WHITE
  doc.text(title, 20, 50);  // 20px left (aligned with table), 50px top
  
  // Add optional subtitle if provided
  if (subtitle) {
    doc.setFont('Roboto', 'normal');
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);  // WHITE
    doc.text(subtitle, 20, 68);  // Aligned with title
  }
  
  // Draw white content container (with rounded corners)
  const hasSubtitle = subtitle && subtitle.trim().length > 0;
  const containerTop = hasSubtitle ? 78 : 62;  // Reduced from 80/65
  const containerMargin = {
    top: containerTop,
    bottom: 25,
    left: 10,
    right: 10,
  };
  const containerX = containerMargin.left;
  const containerY = containerTop;
  const containerWidth = pageWidth - containerMargin.left - containerMargin.right;
  const containerHeight = pageHeight - containerMargin.top - containerMargin.bottom;
  const cornerRadius = 8;
  
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(containerX, containerY, containerWidth, containerHeight, cornerRadius, cornerRadius, 'F');
  
  // Add footer text OUTSIDE container (on gradient background)
  const footerY = pageHeight - (containerMargin.bottom / 2) + 3;
  const footerLeftX = containerMargin.left + 5;
  const footerRightX = pageWidth - containerMargin.right - 5;
  
  doc.setFont('Roboto', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(220, 230, 255);  // Light/subtle white
  
  // Left aligned: date range + copyright
  const formattedDateRange = formatDateRangeShort(dateRange);
  const dateText = formattedDateRange ? `Data ${formattedDateRange}  ` : '';
  doc.text(`${dateText}© Doctivity Health Proprietary & Confidential Do not Share or Reproduce`, footerLeftX, footerY);
  
  // Right aligned: website
  doc.text('www.DoctivityHealth.com', footerRightX, footerY, { align: 'right' });
  
  // Return container bounds for content positioning
  return {
    y: containerY + 10,  // Y position inside container for content (reduced from +15)
    maxY: pageHeight - containerMargin.bottom - 20,  // Maximum Y before footer
  };
};

/**
 * Draws a continuation page (pages 2+) without title/subtitle
 * Container starts higher since there's no title area needed
 * 
 * @param {jsPDF} doc - The jsPDF document instance
 * @param {Object} options - Options including pdfType
 * @returns {number} - Y position for content
 */
export const drawContinuationPage = (doc, options = {}) => {
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const { dateRange } = options;
  
  // Create gradient using canvas for smooth result
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = Math.ceil(pageHeight);
  
  const ctx = canvas.getContext('2d');
  const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
  gradient.addColorStop(0, '#18b095');  // Teal at TOP
  gradient.addColorStop(1, '#348cf5');  // Blue at BOTTOM
  
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  const imgData = canvas.toDataURL('image/png');
  doc.addImage(imgData, 'PNG', 0, 0, pageWidth, pageHeight);
  
  // Draw white angled section across top for logo area
  const { pdfType = 'printable' } = options;
  const anglePercent = pdfType === 'slideshow' ? 0.083 : 0.05;
  const angleEndY = pageHeight * anglePercent;
  const angleStartY = 10;
  
  doc.setFillColor(255, 255, 255);
  
  const points = [
    [pageWidth, 0],
    [0, angleEndY],
    [-pageWidth, angleStartY - angleEndY],
    [0, -angleStartY],
  ];
  doc.lines(points, 0, 0, [1, 1], 'F', true);
  
  // Container starts at 40px on continuation pages (no title/subtitle)
  const containerTop = 40;
  const containerMargin = {
    top: containerTop,
    bottom: 30,
    left: 10,
    right: 10,
  };
  const containerX = containerMargin.left;
  const containerY = containerTop;
  const containerWidth = pageWidth - containerMargin.left - containerMargin.right;
  const containerHeight = pageHeight - containerMargin.top - containerMargin.bottom;
  const cornerRadius = 8;
  
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(containerX, containerY, containerWidth, containerHeight, cornerRadius, cornerRadius, 'F');
  
  // Add footer text
  const footerY = pageHeight - (containerMargin.bottom / 2) + 4;
  const footerLeftX = containerMargin.left + 5;
  const footerRightX = pageWidth - containerMargin.right - 5;
  
  doc.setFont('Roboto', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(255, 255, 255);
  
  // Left aligned: date range + copyright
  const formattedDateRange = formatDateRangeShort(dateRange);
  const dateText = formattedDateRange ? `Data ${formattedDateRange}  ` : '';
  doc.text(`${dateText}© Doctivity Health Proprietary & Confidential Do not Share or Reproduce`, footerLeftX, footerY);
  doc.text('www.DoctivityHealth.com', footerRightX, footerY, { align: 'right' });
  
  return containerY + 15;  // Return Y position inside container for content
};

/**
 * Draws the page footer with legend and confidentiality notice
 * Matches the Doctivity mockup design
 * 
 * @param {jsPDF} doc - The jsPDF document instance
 * @param {Array} legendItems - Array of {name, color, isAffiliatedIndicator} objects for legend
 * @param {Object} options - Options including pdfType
 */
export const drawPageFooterWithLegend = (doc, legendItems = [], options = {}) => {
  const { pdfType = 'printable' } = options;
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const styles = PAGE_FOOTER_STYLES[pdfType] || PAGE_FOOTER_STYLES.printable;
  const settings = getSettingsByType(pdfType);
  
  const footerY = pageHeight - styles.height;
  
  // Draw light gray footer background
  doc.setFillColor(...PAGE_FOOTER_STYLES.bgColor);
  doc.rect(0, footerY, pageWidth, styles.height, 'F');
  
  // Draw top border line
  doc.setDrawColor(...PAGE_FOOTER_STYLES.borderColor);
  doc.setLineWidth(0.5);
  doc.line(0, footerY, pageWidth, footerY);
  
  // Draw legend if items provided
  if (legendItems.length > 0) {
    let legendX = settings.margin.left;
    const legendY = footerY + styles.legendY;
    const boxSize = pdfType === 'slideshow' ? 10 : 8;
    
    doc.setFontSize(styles.fontSize);
    
    legendItems.forEach((item, index) => {
      if (item.isAffiliatedIndicator) {
        // Draw the hub icon indicator (small circle with spokes)
        const iconSize = boxSize * 0.8;
        const cx = legendX + iconSize / 2 + 2;
        const cy = legendY + boxSize / 2;
        
        // Draw center circle
        doc.setFillColor(32, 131, 244); // Blue
        doc.circle(cx, cy, iconSize / 3, 'F');
        
        // Draw small spokes
        doc.setDrawColor(32, 131, 244);
        doc.setLineWidth(0.8);
        for (let i = 0; i < 4; i++) {
          const angle = (i * Math.PI) / 2;
          const innerR = iconSize / 3;
          const outerR = iconSize / 2;
          doc.line(
            cx + Math.cos(angle) * innerR,
            cy + Math.sin(angle) * innerR,
            cx + Math.cos(angle) * outerR,
            cy + Math.sin(angle) * outerR
          );
        }
        
        // Label
        doc.setTextColor(60, 60, 60);
        doc.setFont('Roboto', 'normal');
        doc.text('Affiliated', legendX + iconSize + 8, legendY + boxSize - 1);
        legendX += iconSize + 8 + doc.getTextWidth('Affiliated') + 20;
      } else {
        // Draw color box
        doc.setFillColor(...item.color);
        doc.rect(legendX, legendY, boxSize, boxSize, 'F');
        
        // Draw label
        doc.setTextColor(60, 60, 60);
        doc.setFont('Roboto', 'normal');
        doc.text(item.name, legendX + boxSize + 4, legendY + boxSize - 1);
        
        legendX += boxSize + 4 + doc.getTextWidth(item.name) + 20;
      }
    });
  }
  
  // Draw confidentiality notice
  doc.setFontSize(styles.confidentialFontSize);
  doc.setTextColor(...PAGE_FOOTER_STYLES.textColor);
  doc.setFont('Roboto', 'normal');
  doc.text(PAGE_FOOTER_STYLES.confidentialText, settings.margin.left, footerY + styles.confidentialY);
};

/**
 * Draws a generic legend at the top of the chart
 * 
 * @param {jsPDF} doc - The jsPDF document instance
 * @param {Array} items - Array of {name, color} objects
 * @param {number} x - Starting X position
 * @param {number} y - Starting Y position
 * @param {number} maxWidth - Maximum width available
 * @param {string} pdfType - 'slideshow' or 'printable'
 * @returns {number} - New Y position after legend
 */
export const drawChartLegend = (doc, items, x, y, maxWidth, pdfType = 'printable') => {
  const settings = getSettingsByType(pdfType);
  const boxSize = pdfType === 'slideshow' ? 10 : 8;
  const gap = pdfType === 'slideshow' ? 6 : 6;
  let currentX = x;
  let currentY = y;
  
  doc.setFontSize(settings.chart.fontSize.legend);
  
  items.forEach((item, idx) => {
    const labelWidth = doc.getTextWidth(item.name);
    const itemWidth = boxSize + 4 + labelWidth + gap;
    
    // Wrap to next line if needed
    if (currentX + itemWidth > x + maxWidth && idx > 0) {
      currentX = x;
      currentY += pdfType === 'slideshow' ? 14 : 12;
    }
    
    // Draw color box
    doc.setFillColor(...item.color);
    doc.rect(currentX, currentY, boxSize, boxSize, 'F');
    
    // Draw label
    doc.setTextColor(60, 60, 60);
    doc.setFont('Roboto', 'normal');
    doc.text(item.name, currentX + boxSize + 4, currentY + (pdfType === 'slideshow' ? 8 : 6));
    
    currentX += itemWidth + (pdfType === 'slideshow' ? 10 : 8);
  });
  
  return currentY + (pdfType === 'slideshow' ? 16 : 14);
};

/**
 * Draw location type legend (for Projected Patients)
 */
export const drawLocationTypeLegend = (doc, x, y, maxWidth, pdfType = 'printable') => {
  const legendItems = getLegendByType(pdfType);
  return drawChartLegend(doc, legendItems, x, y, maxWidth, pdfType);
};

/**
 * Draw affiliation legend (for Shared Affiliation)
 */
export const drawAffiliationLegend = (doc, x, y, maxWidth, pdfType = 'printable') => {
  const items = [
    { name: 'Affiliated', color: AFFILIATION_COLORS.affiliated },
    { name: 'Non-Affiliated', color: AFFILIATION_COLORS.nonAffiliated },
  ];
  return drawChartLegend(doc, items, x, y, maxWidth, pdfType);
};

/**
 * Draw payor legend (for Payors chart)
 */
export const drawPayorLegend = (doc, payors, x, y, maxWidth, pdfType = 'printable') => {
  const items = payors.map((payor, idx) => ({
    name: payor.name,
    color: payor.color || DEFAULT_PAYOR_COLORS[idx] || DEFAULT_PAYOR_COLORS[3],
  }));
  // Add "Other" 
  items.push({ name: 'Other Payors', color: DEFAULT_PAYOR_COLORS[3] });
  return drawChartLegend(doc, items, x, y, maxWidth, pdfType);
};

/**
 * Draws an affiliated provider icon (hub icon representation)
 * Since we can't use Material Icons in PDF, we draw a simple indicator
 * 
 * @param {jsPDF} doc - The jsPDF document instance
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {Array} color - RGB color array
 * @param {string} pdfType - 'slideshow' or 'printable'
 */
const drawAffiliatedIndicator = (doc, x, y, color, pdfType = 'printable') => {
  const scale = pdfType === 'slideshow' ? 1 : 0.7;
  const circleRadius = 3 * scale;
  const outerRadius = 5 * scale;
  const offset = 4 * scale;
  
  // Draw a small filled circle as the hub indicator
  doc.setFillColor(...color);
  doc.circle(x + offset, y + offset, circleRadius, 'F');
  
  // Draw small lines radiating out (simplified hub icon)
  doc.setDrawColor(...color);
  doc.setLineWidth(0.5 * scale);
  const cx = x + offset;
  const cy = y + offset;
  for (let i = 0; i < 4; i++) {
    const angle = (i * Math.PI) / 2;
    doc.line(
      cx + Math.cos(angle) * circleRadius,
      cy + Math.sin(angle) * circleRadius,
      cx + Math.cos(angle) * outerRadius,
      cy + Math.sin(angle) * outerRadius
    );
  }
};

/**
 * Check if we need to add a new page
 */
const checkPageBreak = (doc, currentY, barHeight, barGap, options = {}) => {
  const pdfType = options.pdfType || 'printable';
  const settings = getSettingsByType(pdfType);
  
  const pageHeight = doc.internal.pageSize.height;
  const pageWidth = doc.internal.pageSize.width;
  const footerHeight = settings.chart.footerHeight;
  const legendHeight = options.showLegend ? settings.chart.legendHeight : 0;
  const bottomMargin = footerHeight + legendHeight + settings.margin.bottom;
  
  if (currentY + barHeight + barGap > pageHeight - bottomMargin) {
    // Draw legend at bottom of current page before adding new page
    if (options.showLegend && options.drawLegend) {
      const effectiveWidth = pageWidth - settings.margin.left - settings.margin.right;
      const legendY = pageHeight - footerHeight - legendHeight;
      options.drawLegend(doc, settings.margin.left, legendY, effectiveWidth);
    }
    
    doc.addPage();
    return settings.margin.top;
  }
  return currentY;
};

/**
 * Truncate text to fit within a given pixel width using actual font metrics
 */
const truncateText = (doc, text, maxWidth) => {
  if (!text) return '';
  if (doc.getTextWidth(text) <= maxWidth) return text;
  let truncated = text;
  while (truncated.length > 1 && doc.getTextWidth(truncated + '…') > maxWidth) {
    truncated = truncated.slice(0, -1);
  }
  return truncated + '…';
};

/**
 * Draws the Projected Patients horizontal bar chart
 * Matches the ProviderProjectedPatientsChart React component
 * 
 * @param {jsPDF} doc - The jsPDF document instance
 * @param {Array} data - Array of claim rows from CLAIMS_PER_PROVIDER
 * @param {Object} options - Chart rendering options
 * @returns {number} - New Y position after chart
 */
export const drawProjectedPatientsChart = (doc, data, options = {}) => {
  const {
    x = null,
    y = 80,
    width = null,
    maxRows = 200,
    allData = null,  // Optional: pass all data for consistent column widths across pages
    showLegend = true,
    patientType = 'ALL_PATIENTS',
    claimsType = 'providers',
    pdfType = 'printable',
    filters = {},  // Pass filters to check for single specialty
  } = options;

  // Get settings based on pdfType
  const settings = getSettingsByType(pdfType);
  
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  
  // Table dimensions
  const startX = x || 20;  // Left margin inside container
  const tableWidth = width || pageWidth - 40;  // Full width minus margins
  
  // Row settings - reduced to fit more content
  const rowHeight = pdfType === 'slideshow' ? 30 : 26;
  const headerHeight = pdfType === 'slideshow' ? 24 : 20;
  const fontSize = pdfType === 'slideshow' ? 10 : 8;
  const nameFontSize = pdfType === 'slideshow' ? 11 : 9;
  const headerFontSize = pdfType === 'slideshow' ? 8 : 6;
  
  // Colors - Blue for affiliated, Red for non-affiliated
  const affiliatedBarColor = [83, 145, 213];  // Blue
  const nonAffiliatedBarColor = [255, 107, 107];  // Red - rgb(255, 107, 107)
  const headerBgColor = [235, 241, 248];  // Light blue-gray
  const headerTextColor = [100, 116, 139];  // Gray
  const rowBgColor = [255, 255, 255];  // White
  // Alternating row: header color at ~40% opacity (blended with white)
  const altRowBgColor = [243, 247, 251];  // Lighter version of header color
  
  let currentY = y;
  
  // Check if this is captured claims
  const isCapturedClaims = patientType === 'CAPTURED_CLAIMS' || patientType === 'PEDIATRIC_PATIENTS';
  
  // Check if single specialty is filtered (hide specialty column if so)
  const hasSingleSpecialty = (
    (filters.specialty && filters.specialty !== 'All Specialties' && filters.specialty !== 'All') ||
    (filters.taxonomies && Array.isArray(filters.taxonomies) && filters.taxonomies.length === 1)
  );
  
  // Check if care setting is 'All' - show SOC column if so
  const showSOCColumn = !filters.careSetting || filters.careSetting === 'All' || filters.careSetting === '';
  
  // SOC abbreviation mapping based on location_type
  const getSOCAbbreviation = (locationType) => {
    switch (locationType) {
      case 1: return 'ASC';
      case 2: return 'Outpt';
      case 3: return 'Office';
      case 4: return 'Inpt';
      case 5: return 'Lab';
      case 6: return 'UC';
      default: return '';
    }
  };
  
  // Find max value for scaling the stacked bars
  // Use allData if provided (for consistent bar scaling across pages), otherwise use data
  const dataForMax = allData || data;
  const maxValue = dataForMax.reduce((max, row) => {
    const total = parseInt(row.total, 10) || 0;
    return Math.max(max, total);
  }, 100);

  // === Measure actual data to auto-size name column ===
  // For organizations: Organization Name, Specialty (no separate org column)
  // For providers: Provider Name, Specialty (optional), Organization
  const isOrganizations = claimsType === 'organizations';
  const showSpecialtyColumn = !hasSingleSpecialty || isOrganizations;
  
  // Measure the widest name text
  doc.setFontSize(fontSize);
  const rowsToMeasure = (allData || data).slice(0, maxRows);

  let maxNameW = 0;
  rowsToMeasure.forEach((rowData) => {
    let name;
    if (isOrganizations) {
      name = rowData.organization?.name || rowData.name || 'Unknown Organization';
    } else {
      const fn = rowData.provider?.first_name || '';
      const ln = rowData.provider?.last_name || '';
      name = `${ln}, ${fn}`.trim().replace(/^,\s*/, '').replace(/,\s*$/, '') || 'Unknown Provider';
    }
    doc.setFont('Roboto', 'bold');
    maxNameW = Math.max(maxNameW, doc.getTextWidth(name));
  });

  // Add padding to measured width to account for rendering offsets
  // Rendering uses +5 for left offset and -10 for truncation width
  const cellPadding = 30;
  maxNameW += cellPadding;

  // Column layout:
  // For providers: Icon (3%) | Name (stretch to data, max 40%) | Specialty (if shown) | Org (2x specialty) | SOC (if All care setting) | Patients+%+Chart combined
  // For organizations: Icon (3%) | Name (max 55%) | Specialty | SOC (if All care setting) | Patients+%+Chart combined
  
  const iconW = 0.03;
  const socW = showSOCColumn ? 0.06 : 0;  // SOC column width when shown
  const patientsW = 0.08;   // Increased from 0.05 for large numbers
  const pctNumberW = 0.06;  // Increased from 0.04 for percentage
  const chartW = 0.18;      // Wider bar (+50px equivalent)
  const combinedRightW = patientsW + pctNumberW + chartW;  // Combined width for last 3 columns
  
  let namePct, specialtyPct, orgPct;
  
  if (isOrganizations) {
    // For organizations: name stretches to data with max 55%, specialty (facility type) is fixed width
    const neededNamePct = maxNameW / tableWidth;
    namePct = Math.min(neededNamePct, 0.55);  // Max 55% (reduced to make room for SOC)
    namePct = Math.max(namePct, 0.20);  // Min 20%
    
    // Fixed width for facility type column - fits "Equipment Supplies"
    specialtyPct = 0.12;
    orgPct = 0;
  } else {
    // For providers: name stretches to data with max 40%
    // Calculate what percentage the measured width needs
    const neededNamePct = maxNameW / tableWidth;
    namePct = Math.min(neededNamePct, 0.40);  // Max 40%
    namePct = Math.max(namePct, 0.20);  // Min 20% to ensure names aren't too cramped
    
    const remainingFlex = 1.0 - iconW - namePct - socW - combinedRightW;
    
    if (showSpecialtyColumn) {
      // Org gets 2x specialty width
      specialtyPct = remainingFlex / 3;
      orgPct = remainingFlex * 2 / 3;
    } else {
      // No specialty column - org gets all remaining space
      specialtyPct = 0;
      orgPct = remainingFlex;
    }
  }

  // Build column layout
  let columns;
  if (isOrganizations) {
    // Organizations: icon, name (org name), specialty, [soc], patients, pct, chart
    // Chart column dynamically extends to edge of table
    const chartStart = iconW + namePct + specialtyPct + socW + patientsW + pctNumberW;
    const orgChartW = 1.0 - chartStart;  // Extend to edge
    columns = {
      icon:         { start: 0,                                                   width: iconW },
      name:         { start: iconW,                                               width: namePct },
      specialty:    { start: iconW + namePct,                                     width: specialtyPct },
      ...(showSOCColumn && { soc: { start: iconW + namePct + specialtyPct, width: socW } }),
      patients:     { start: iconW + namePct + specialtyPct + socW,               width: patientsW },
      pctNumber:    { start: iconW + namePct + specialtyPct + socW + patientsW,   width: pctNumberW },
      chart:        { start: chartStart, width: orgChartW },
    };
  } else if (showSpecialtyColumn) {
    // Providers with specialty column: icon, name, specialty, organization, [soc], patients, pct, chart
    columns = {
      icon:         { start: 0,                                                             width: iconW },
      name:         { start: iconW,                                                         width: namePct },
      specialty:    { start: iconW + namePct,                                               width: specialtyPct },
      organization: { start: iconW + namePct + specialtyPct,                                width: orgPct },
      ...(showSOCColumn && { soc: { start: iconW + namePct + specialtyPct + orgPct, width: socW } }),
      patients:     { start: iconW + namePct + specialtyPct + orgPct + socW,                width: patientsW },
      pctNumber:    { start: iconW + namePct + specialtyPct + orgPct + socW + patientsW,    width: pctNumberW },
      chart:        { start: iconW + namePct + specialtyPct + orgPct + socW + patientsW + pctNumberW, width: chartW },
    };
  } else {
    // Providers without specialty column: icon, name, organization, [soc], patients, pct, chart
    columns = {
      icon:         { start: 0,                                                    width: iconW },
      name:         { start: iconW,                                                width: namePct },
      organization: { start: iconW + namePct,                                      width: orgPct },
      ...(showSOCColumn && { soc: { start: iconW + namePct + orgPct, width: socW } }),
      patients:     { start: iconW + namePct + orgPct + socW,                      width: patientsW },
      pctNumber:    { start: iconW + namePct + orgPct + socW + patientsW,          width: pctNumberW },
      chart:        { start: iconW + namePct + orgPct + socW + patientsW + pctNumberW, width: chartW },
    };
  }

  // === Draw column headers ===
  const drawHeaders = () => {
    doc.setFillColor(...headerBgColor);
    // Draw rounded rect for full header, then cover bottom corners with a flat rect
    doc.roundedRect(startX, currentY, tableWidth, headerHeight, 4, 4, 'F');
    // Cover bottom rounded corners with a flat rect on the bottom half
    doc.rect(startX, currentY + headerHeight / 2, tableWidth, headerHeight / 2, 'F');
    
    doc.setFontSize(headerFontSize);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(...headerTextColor);
    
    const headerY = currentY + headerHeight / 2 + 3;
    
    // Build headers based on report type and whether specialty column is shown
    // Last 3 columns (patients/claims, %, chart) share one header
    const rightColumnHeader = isCapturedClaims ? 'CLAIMS & MARKET %' : 'PATIENTS & MARKET %';
    let headers;
    if (isOrganizations) {
      headers = [
        { text: 'ORGANIZATION', col: 'name' },
        { text: 'FACILITY TYPE', col: 'specialty' },
        ...(showSOCColumn ? [{ text: 'SOC', col: 'soc' }] : []),
        { text: rightColumnHeader, col: 'patients', span: 3 },  // Spans patients, pct, chart
      ];
    } else if (showSpecialtyColumn) {
      headers = [
        { text: 'PROVIDER', col: 'name' },
        { text: 'SPECIALTY', col: 'specialty' },
        { text: 'ORGANIZATION', col: 'organization' },
        ...(showSOCColumn ? [{ text: 'SOC', col: 'soc' }] : []),
        { text: rightColumnHeader, col: 'patients', span: 3 },  // Spans patients, pct, chart
      ];
    } else {
      // No specialty column
      headers = [
        { text: 'PROVIDER', col: 'name' },
        { text: 'ORGANIZATION', col: 'organization' },
        ...(showSOCColumn ? [{ text: 'SOC', col: 'soc' }] : []),
        { text: rightColumnHeader, col: 'patients', span: 3 },  // Spans patients, pct, chart
      ];
    }
    
    headers.forEach(header => {
      const colX = startX + columns[header.col].start * tableWidth + 5;
      let colWidth;
      if (header.span === 3) {
        // Combined header spans patients + pctNumber + chart columns
        colWidth = (patientsW + pctNumberW + chartW) * tableWidth - 10;
      } else {
        colWidth = columns[header.col].width * tableWidth - 10;
      }
      const headerText = truncateText(doc, header.text, colWidth);
      doc.text(headerText, colX, headerY);
    });
    
    currentY += headerHeight + 2;
  };

  // === Draw a single row ===
  const drawRow = (rowData, index) => {
    const total = parseInt(rowData.total, 10) || 0;
    const locationType = rowData.location_type || 3;
    
    // Handle both providers and organizations
    let displayName = '';
    let isAffiliated = false;
    let orgName = '';
    let specialty = '';
    
    if (claimsType === 'organizations') {
      const org = rowData.organization || rowData.dataValues?.organization || {};
      const rawName = org.name || org.dataValues?.name || rowData.name || 'Unknown Organization';
      displayName = rawName.toUpperCase();  // ALL CAPS for org names
      isAffiliated = rowData.affiliated === true || 
                    org.is_affiliated === true || 
                    (org.affiliated?.length > 0);
      // Use facility_type for organizations - check nested org first since that's where it comes from
      const rawFacilityType = org.facility_type || org.dataValues?.facility_type ||
                          rowData.facility_type || rowData.dataValues?.facility_type || '';
      specialty = toTitleCase(rawFacilityType);
    } else {
      // Provider data structure
      const firstName = rowData.provider?.first_name || '';
      const lastName = rowData.provider?.last_name || '';
      const rawName = `${lastName}, ${firstName}`.trim().replace(/^,\s*/, '').replace(/,\s*$/, '') || 'Unknown Provider';
      displayName = rawName.toUpperCase();  // ALL CAPS for provider names
      
      // Check affiliation - NULL means not affiliated
      isAffiliated = rowData.affiliated === true || 
                    rowData.provider?.is_affiliated === true || 
                    (rowData.provider?.clients?.length > 0);
      
      // Organization from provider.location.organization
      const rawOrg = rowData.provider?.location?.organization?.name ||
                rowData.organization_name ||
                rowData.location?.organization?.name || '';
      orgName = toTitleCase(rawOrg);
      
      // Specialty from taxonomy using SpecialtyUtil
      const taxonomy = rowData.provider?.taxonomy || rowData.taxonomy;
      if (taxonomy) {
        specialty = SpecialtyUtil.getNameFromTaxonomy(taxonomy) || '';
      } else {
        // Fallback to other specialty fields
        specialty = toTitleCase(rowData.provider?.specialty || rowData.specialty || '');
      }
    }
    
    // Row background
    const bgColor = index % 2 === 0 ? rowBgColor : altRowBgColor;
    doc.setFillColor(...bgColor);
    doc.rect(startX, currentY, tableWidth, rowHeight, 'F');
    
    const textY = currentY + rowHeight / 2 + 3;
    const iconSize = pdfType === 'slideshow' ? 12 : 10;
    
    // === Icon column (separate column for affiliation icon) ===
    const iconX = startX + columns.icon.start * tableWidth + 5;
    if (isAffiliated) {
      // Hub icon for affiliated (matching mockup)
      drawAffiliatedHubIcon(doc, iconX, currentY + (rowHeight - iconSize) / 2, iconSize, affiliatedBarColor);
    }
    // Non-affiliated: leave blank (no icon)
    
    // === Name column (Provider name or Organization name) ===
    const nameX = startX + columns.name.start * tableWidth + 5;
    doc.setFontSize(nameFontSize);
    doc.setFont('Roboto', 'normal');
    doc.setTextColor(33, 37, 41);
    const nameColWidth = columns.name.width * tableWidth - 10;
    const nameToDraw = truncateText(doc, displayName, nameColWidth);
    doc.text(nameToDraw, nameX, textY);
    
    // === Specialty column (only if shown) ===
    if (showSpecialtyColumn && columns.specialty) {
      const specialtyX = startX + columns.specialty.start * tableWidth + 5;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      const specColWidth = columns.specialty.width * tableWidth - 10;
      const specToDraw = truncateText(doc, specialty || '-', specColWidth);
      doc.text(specToDraw, specialtyX, textY);
    }
    
    // === Organization column (only for providers) ===
    if (!isOrganizations && columns.organization) {
      const orgX = startX + columns.organization.start * tableWidth + 5;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      const orgColWidth = columns.organization.width * tableWidth - 10;
      const orgToDraw = truncateText(doc, orgName || '-', orgColWidth);
      doc.text(orgToDraw, orgX, textY);
    }
    
    // === SOC column (only if care setting is 'All') ===
    if (showSOCColumn && columns.soc) {
      const socX = startX + columns.soc.start * tableWidth + 5;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      const socColWidth = columns.soc.width * tableWidth - 10;
      const socAbbrev = getSOCAbbreviation(locationType);
      const socToDraw = truncateText(doc, socAbbrev || '-', socColWidth);
      doc.text(socToDraw, socX, textY);
    }
    
    // === Patients/Claims column ===
    const patientsX = startX + columns.patients.start * tableWidth + 2;
    
    // Person icon - only for projected patients, not captured claims
    if (!isCapturedClaims) {
      drawPersonIcon(doc, patientsX, currentY + (rowHeight - iconSize) / 2, iconSize, [156, 163, 175]);
    }
    
    // Patient/Claims count
    doc.setFontSize(fontSize);
    doc.setFont('RobotoLight', 'normal');
    doc.setTextColor(75, 85, 99);
    const countText = total > 0 ? total.toLocaleString() : '<11';
    const countX = isCapturedClaims ? patientsX : patientsX + iconSize + 2;
    doc.text(countText, countX, textY);
    
    // === Percentage number column (next to patients) ===
    const pctNumX = startX + columns.pctNumber.start * tableWidth + 5;
    doc.setFontSize(fontSize);
    doc.setFont('RobotoLight', 'normal');
    doc.setTextColor(75, 85, 99);
    
    let pctText = '-';
    if (rowData.aggregateClaimsPercentage !== null && rowData.aggregateClaimsPercentage !== undefined) {
      const pctValue = rowData.aggregateClaimsPercentage * 100;
      pctText = `${pctValue.toFixed(2)}%`;
    }
    doc.text(pctText, pctNumX, textY);
    
    // === Chart column (bar only, no percentage text) ===
    const chartX = startX + columns.chart.start * tableWidth + 15;  // Extra spacing from percentage
    const barWidth = columns.chart.width * tableWidth - 20;  // Reduce width to account for spacing
    const barHeight = pdfType === 'slideshow' ? 20 : 18;  // Taller bars
    const barY = currentY + (rowHeight - barHeight) / 2;
    
    // Calculate bar width based on total relative to max
    const scale = maxValue > 0 ? (total / maxValue) : 0;
    const scaledBarWidth = barWidth * scale;
    
    // Draw single bar - red if affiliated, blue if non-affiliated
    // Use subtle rounded corners (2px radius)
    const barRadius = 2;
    if (scaledBarWidth > 0) {
      const barColor = isAffiliated ? affiliatedBarColor : nonAffiliatedBarColor;
      doc.setFillColor(...barColor);
      doc.roundedRect(chartX, barY, Math.max(scaledBarWidth, barRadius * 2), barHeight, barRadius, barRadius, 'F');
    }
    
    currentY += rowHeight;
  };

  // === Draw legend (matching mockup: hub icon + "Affiliated", blue box + "Affiliated", red box + "Non-Affiliated") ===
  const drawTableLegend = () => {
    const legendY = currentY + 10;
    const iconSize = pdfType === 'slideshow' ? 12 : 10;
    const boxSize = pdfType === 'slideshow' ? 14 : 10;
    const legendFontSize = pdfType === 'slideshow' ? 10 : 8;
    let legendX = startX + 10;
    
    console.log('Legend drawing at:', { legendX, legendY, startX, currentY, pdfType });
    
    doc.setFontSize(legendFontSize);
    doc.setFont('Roboto', 'normal');
    doc.setTextColor(33, 37, 41);
    
    // 1. Affiliated hub icon + "Affiliated" (red)
    console.log('Drawing hub icon at:', legendX, legendY);
    drawAffiliatedHubIcon(doc, legendX, legendY, iconSize, affiliatedBarColor);
    legendX += iconSize + 4;
    doc.text('Affiliated', legendX, legendY + iconSize / 2 + 3);
    legendX += doc.getTextWidth('Affiliated') + 20;
    
    // 2. Red box + "Affiliated"
    console.log('Drawing red box at:', legendX, legendY);
    doc.setFillColor(...affiliatedBarColor);
    doc.rect(legendX, legendY + 1, boxSize, boxSize - 2, 'F');
    legendX += boxSize + 4;
    doc.text('Affiliated', legendX, legendY + iconSize / 2 + 3);
    legendX += doc.getTextWidth('Affiliated') + 20;
    
    // 3. Blue box + "Non-Affiliated"
    console.log('Drawing blue box at:', legendX, legendY);
    doc.setFillColor(...nonAffiliatedBarColor);
    doc.rect(legendX, legendY + 1, boxSize, boxSize - 2, 'F');
    legendX += boxSize + 4;
    doc.text('Non-Affiliated', legendX, legendY + iconSize / 2 + 3);
    
    console.log('Legend complete');
    currentY = legendY + iconSize + 15;
  };

  // Draw headers
  drawHeaders();
  
  // Draw each row
  const rowsToRender = data.slice(0, maxRows);
  rowsToRender.forEach((rowData, index) => {
    drawRow(rowData, index);
  });

  return currentY + 10;
};

/**
 * Draw affiliated hub icon (network/connection icon matching Material Icons "hub")
 * Central circle with lines radiating to smaller outer circles
 */
const drawAffiliatedHubIcon = (doc, x, y, size, color) => {
  doc.setFillColor(...color);
  doc.setDrawColor(...color);
  doc.setLineWidth(size * 0.08);
  
  const centerX = x + size / 2;
  const centerY = y + size / 2;
  const centerRadius = size * 0.18;
  const outerRadius = size * 0.10;
  const orbitRadius = size * 0.38;
  
  // Draw lines from center to outer nodes first (so circles draw on top)
  const nodeCount = 5;
  for (let i = 0; i < nodeCount; i++) {
    const angle = (i * 2 * Math.PI / nodeCount) - Math.PI / 2; // Start from top
    const nodeX = centerX + Math.cos(angle) * orbitRadius;
    const nodeY = centerY + Math.sin(angle) * orbitRadius;
    doc.line(centerX, centerY, nodeX, nodeY);
  }
  
  // Draw center circle
  doc.circle(centerX, centerY, centerRadius, 'F');
  
  // Draw outer node circles
  for (let i = 0; i < nodeCount; i++) {
    const angle = (i * 2 * Math.PI / nodeCount) - Math.PI / 2; // Start from top
    const nodeX = centerX + Math.cos(angle) * orbitRadius;
    const nodeY = centerY + Math.sin(angle) * orbitRadius;
    doc.circle(nodeX, nodeY, outerRadius, 'F');
  }
};

/**
 * Draw person/user icon
 */
const drawPersonIcon = (doc, x, y, size, color) => {
  doc.setFillColor(...color);
  
  // Head (circle)
  const headRadius = size * 0.25;
  const headY = y + headRadius + 1;
  doc.circle(x + size / 2, headY, headRadius, 'F');
  
  // Body (arc/shoulders)
  const bodyY = headY + headRadius + 1;
  const bodyWidth = size * 0.7;
  const bodyHeight = size * 0.4;
  doc.ellipse(x + size / 2, bodyY + bodyHeight / 2, bodyWidth / 2, bodyHeight / 2, 'F');
};

/**
 * Draws affiliation summary badges (like "~0% Affiliated" "~100% Non-Affiliated")
 */
export const drawAffiliationSummary = (doc, counts, x, y, options = {}) => {
  if (!counts || !counts.total) return y;
  
  const { pdfType = 'printable' } = options;
  const scale = pdfType === 'slideshow' ? 1 : 0.7;
  
  const affiliatedPct = ((counts.total_affiliated / counts.total) * 100).toFixed(0);
  const nonAffiliatedPct = (100 - affiliatedPct).toFixed(0);
  
  doc.setFontSize(pdfType === 'slideshow' ? 10 : 7);
  doc.setFont('Roboto', 'normal');
  
  const badgeHeight = 22 * scale;
  const badgeWidth1 = 100 * scale;
  const badgeWidth2 = 120 * scale;
  const textPadding = 8 * scale;
  const textY = y + 15 * scale;
  const badgeGap = 10 * scale;
  
  // Affiliated badge
  doc.setFillColor(230, 243, 255);
  doc.rect(x, y, badgeWidth1, badgeHeight, 'F');
  doc.setTextColor(...AFFILIATION_COLORS.affiliated);
  doc.text(`~${affiliatedPct}% Affiliated`, x + textPadding, textY);
  
  // Non-affiliated badge
  doc.setFillColor(255, 235, 238);
  doc.rect(x + badgeWidth1 + badgeGap, y, badgeWidth2, badgeHeight, 'F');
  doc.setTextColor(...AFFILIATION_COLORS.nonAffiliated);
  doc.text(`~${nonAffiliatedPct}% Non-Affiliated`, x + badgeWidth1 + badgeGap + textPadding, textY);
  
  doc.setTextColor(60, 60, 60);
  
  return y + badgeHeight + (pdfType === 'slideshow' ? 10 : 6);
};

/**
 * Draws the Shared Affiliation chart in table format (matching Projected Patients UI)
 */
export const drawSharedAffiliationChart = (doc, data, options = {}) => {
  const {
    x = null,
    y = 80,
    width = null,
    maxRows = 200,
    allData = null,  // Optional: pass all data for consistent column widths across pages
    showLegend = true,
    claimsType = 'providers',
    pdfType = 'printable',
    filters = {},
  } = options;

  const settings = getSettingsByType(pdfType);
  const pageWidth = doc.internal.pageSize.width;
  
  // Table dimensions
  const startX = x || 20;
  const tableWidth = width || pageWidth - 40;
  
  // Row settings
  const rowHeight = pdfType === 'slideshow' ? 30 : 26;
  const headerHeight = pdfType === 'slideshow' ? 24 : 20;
  const fontSize = pdfType === 'slideshow' ? 10 : 8;
  const nameFontSize = pdfType === 'slideshow' ? 11 : 9;
  const headerFontSize = pdfType === 'slideshow' ? 8 : 6;
  
  // Colors - Blue for affiliated, Red for non-affiliated
  const affiliatedBarColor = AFFILIATION_COLORS.affiliated;
  const nonAffiliatedBarColor = AFFILIATION_COLORS.nonAffiliated;
  const headerBgColor = [235, 241, 248];
  const headerTextColor = [100, 116, 139];
  const rowBgColor = [255, 255, 255];
  const altRowBgColor = [243, 247, 251];
  
  let currentY = y;
  
  const isOrganizations = claimsType === 'organizations';
  
  // Check if single specialty is filtered (hide specialty column if so)
  const hasSingleSpecialty = (
    (filters.specialty && filters.specialty !== 'All Specialties' && filters.specialty !== 'All') ||
    (filters.taxonomies && Array.isArray(filters.taxonomies) && filters.taxonomies.length === 1)
  );
  const showSpecialtyColumn = !hasSingleSpecialty;
  
  // Find max value for scaling bars - use allData if provided
  const dataForMax = allData || data;
  const maxValue = dataForMax.reduce((max, row) => {
    const total = parseInt(row.shared_patients ?? row.total ?? 0, 10) || 0;
    return Math.max(max, total);
  }, 100);

  // Column layout for Shared Affiliation (no patient count column)
  // For providers: Icon | Provider | Specialty (optional) | Organization | % Affiliated | Bar
  // For organizations: Icon | Organization | Specialty (optional) | % Affiliated | Bar
  const iconW = 0.03;
  const pctAffiliatedW = 0.11;  // Increased for "% AFFILIATED" header
  const chartW = 0.25;  // Adjusted for header space
  
  let namePct, specialtyPct, orgPct;
  if (isOrganizations) {
    if (showSpecialtyColumn) {
      namePct = 0.49;
      specialtyPct = 0.12;  // Reduced - fits "Equipment Supplies"
    } else {
      namePct = 0.61;
      specialtyPct = 0;
    }
    orgPct = 0;
  } else {
    if (showSpecialtyColumn) {
      namePct = 0.24;
      specialtyPct = 0.12;  // Reduced - fits "Equipment Supplies"
      orgPct = 0.25;
    } else {
      namePct = 0.25;
      specialtyPct = 0;
      orgPct = 0.36;
    }
  }
  
  let columns;
  if (isOrganizations) {
    if (showSpecialtyColumn) {
      columns = {
        icon:         { start: 0,                                                   width: iconW },
        name:         { start: iconW,                                               width: namePct },
        specialty:    { start: iconW + namePct,                                     width: specialtyPct },
        pctAffiliated:{ start: iconW + namePct + specialtyPct,                      width: pctAffiliatedW },
        chart:        { start: iconW + namePct + specialtyPct + pctAffiliatedW,     width: chartW },
      };
    } else {
      columns = {
        icon:         { start: 0,                                                   width: iconW },
        name:         { start: iconW,                                               width: namePct },
        pctAffiliated:{ start: iconW + namePct,                                     width: pctAffiliatedW },
        chart:        { start: iconW + namePct + pctAffiliatedW,                    width: chartW },
      };
    }
  } else {
    if (showSpecialtyColumn) {
      columns = {
        icon:         { start: 0,                                                             width: iconW },
        name:         { start: iconW,                                                         width: namePct },
        specialty:    { start: iconW + namePct,                                               width: specialtyPct },
        organization: { start: iconW + namePct + specialtyPct,                                width: orgPct },
        pctAffiliated:{ start: iconW + namePct + specialtyPct + orgPct,                       width: pctAffiliatedW },
        chart:        { start: iconW + namePct + specialtyPct + orgPct + pctAffiliatedW,      width: chartW },
      };
    } else {
      columns = {
        icon:         { start: 0,                                                             width: iconW },
        name:         { start: iconW,                                                         width: namePct },
        organization: { start: iconW + namePct,                                               width: orgPct },
        pctAffiliated:{ start: iconW + namePct + orgPct,                                      width: pctAffiliatedW },
        chart:        { start: iconW + namePct + orgPct + pctAffiliatedW,                     width: chartW },
      };
    }
  }

  // === Draw column headers ===
  const drawHeaders = () => {
    doc.setFillColor(...headerBgColor);
    doc.roundedRect(startX, currentY, tableWidth, headerHeight, 4, 4, 'F');
    doc.rect(startX, currentY + headerHeight / 2, tableWidth, headerHeight / 2, 'F');
    
    doc.setFontSize(headerFontSize);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(...headerTextColor);
    
    const headerY = currentY + headerHeight / 2 + 3;
    let headers;
    if (isOrganizations) {
      headers = [
        { text: 'ORGANIZATION', col: 'name' },
        ...(showSpecialtyColumn ? [{ text: 'FACILITY TYPE', col: 'specialty' }] : []),
        { text: '% AFFILIATED', col: 'pctAffiliated' },
        { text: 'PATIENTS', col: 'chart' },
      ];
    } else {
      headers = [
        { text: 'PROVIDER', col: 'name' },
        ...(showSpecialtyColumn ? [{ text: 'SPECIALTY', col: 'specialty' }] : []),
        { text: 'ORGANIZATION', col: 'organization' },
        { text: '% AFFILIATED', col: 'pctAffiliated' },
        { text: 'PATIENTS', col: 'chart' },
      ];
    }
    
    headers.forEach(header => {
      const colX = startX + columns[header.col].start * tableWidth + 5;
      const colWidth = columns[header.col].width * tableWidth - 10;
      const headerText = truncateText(doc, header.text, colWidth);
      doc.text(headerText, colX, headerY);
    });
    
    currentY += headerHeight + 2;
  };

  // === Draw a single row ===
  const drawRow = (rowData, index) => {
    const totalShared = parseInt(rowData.shared_patients ?? rowData.total ?? 0, 10) || 0;
    const rawAffiliated = rowData.shared_patients_affiliated ?? rowData.affiliated_totals;
    const affiliatedShared = (rawAffiliated === null || rawAffiliated === undefined) 
      ? 0 
      : (parseInt(rawAffiliated, 10) || 0);
    
    const affiliatedPct = totalShared > 0 ? (affiliatedShared / totalShared) * 100 : 0;
    
    // Get display values
    let displayName = '';
    let isAffiliated = false;
    let orgName = '';
    let specialty = '';
    
    if (isOrganizations) {
      const org = rowData.organization || rowData.dataValues?.organization || {};
      const rawName = org.name || org.dataValues?.name || rowData.name || 'Unknown Organization';
      displayName = rawName.toUpperCase();  // ALL CAPS for org names
      isAffiliated = rowData.affiliated === true || 
                    org.is_affiliated === true || 
                    (org.affiliated?.length > 0);
      // Use facility_type for organizations - check nested org first since that's where it comes from
      const rawFacilityType = org.facility_type || org.dataValues?.facility_type ||
                          rowData.facility_type || rowData.dataValues?.facility_type || '';
      specialty = toTitleCase(rawFacilityType);
    } else {
      const firstName = rowData.first_name || rowData.provider?.first_name || '';
      const lastName = rowData.last_name || rowData.provider?.last_name || '';
      const rawName = `${lastName}, ${firstName}`.trim().replace(/^,\s*/, '').replace(/,\s*$/, '') || 'Unknown Provider';
      displayName = rawName.toUpperCase();  // ALL CAPS for provider names
      isAffiliated = rowData.is_affiliated ?? rowData.affiliated ?? rowData.provider?.is_affiliated ?? false;
      const rawOrg = rowData.organization_name || rowData.provider?.location?.organization?.name || '';
      orgName = toTitleCase(rawOrg);
      // Get taxonomy from various possible locations in the data structure
      const taxonomy = rowData.taxonomy || rowData.dataValues?.taxonomy || rowData.provider?.taxonomy;
      if (taxonomy) {
        specialty = SpecialtyUtil.getNameFromTaxonomy(taxonomy) || '';
      } else {
        // Fallback to other specialty fields
        specialty = toTitleCase(rowData.provider?.specialty || rowData.specialty || '');
      }
    }
    
    // Row background
    const bgColor = index % 2 === 0 ? rowBgColor : altRowBgColor;
    doc.setFillColor(...bgColor);
    doc.rect(startX, currentY, tableWidth, rowHeight, 'F');
    
    const textY = currentY + rowHeight / 2 + 3;
    const iconSize = pdfType === 'slideshow' ? 12 : 10;
    
    // === Icon column ===
    const iconX = startX + columns.icon.start * tableWidth + 5;
    if (isAffiliated) {
      drawAffiliatedHubIcon(doc, iconX, currentY + (rowHeight - iconSize) / 2, iconSize, affiliatedBarColor);
    }
    
    // === Name column ===
    const nameX = startX + columns.name.start * tableWidth + 5;
    doc.setFontSize(nameFontSize);
    doc.setFont('Roboto', 'normal');
    doc.setTextColor(33, 37, 41);
    const nameColWidth = columns.name.width * tableWidth - 10;
    const nameToDraw = truncateText(doc, displayName, nameColWidth);
    doc.text(nameToDraw, nameX, textY);
    
    // === Specialty column (conditional) ===
    if (showSpecialtyColumn && columns.specialty) {
      const specialtyX = startX + columns.specialty.start * tableWidth + 5;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      const specColWidth = columns.specialty.width * tableWidth - 10;
      const specToDraw = truncateText(doc, specialty || '-', specColWidth);
      doc.text(specToDraw, specialtyX, textY);
    }
    
    // === Organization column (only for providers) ===
    if (!isOrganizations && columns.organization) {
      const orgX = startX + columns.organization.start * tableWidth + 5;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      const orgColWidth = columns.organization.width * tableWidth - 10;
      const orgToDraw = truncateText(doc, orgName || '-', orgColWidth);
      doc.text(orgToDraw, orgX, textY);
    }
    
    // === % Affiliated column (right aligned with padding) ===
    const pctColWidth = columns.pctAffiliated.width * tableWidth;
    const pctX = startX + columns.pctAffiliated.start * tableWidth + pctColWidth - 10;
    doc.setFontSize(fontSize);
    doc.setFont('RobotoLight', 'normal');
    doc.setTextColor(75, 85, 99);
    const pctText = `${affiliatedPct.toFixed(2)}%`;
    doc.text(pctText, pctX, textY, { align: 'right' });
    
    // === Chart column (stacked bar with labels) ===
    const chartX = startX + columns.chart.start * tableWidth + 5;
    const barWidth = columns.chart.width * tableWidth - 10;
    const barHeight = pdfType === 'slideshow' ? 20 : 18;  // Taller bars
    const barY = currentY + (rowHeight - barHeight) / 2;
    const barRadius = 2;  // Subtle rounded corners
    
    // Scale bar to max value
    const scale = maxValue > 0 ? (totalShared / maxValue) : 0;
    const scaledBarWidth = barWidth * scale;
    
    if (scaledBarWidth > 0) {
      const actualBarWidth = Math.max(scaledBarWidth, barRadius * 2);
      const affiliatedWidth = (affiliatedPct / 100) * actualBarWidth;
      
      // Draw full bar with non-affiliated color (rounded)
      doc.setFillColor(...nonAffiliatedBarColor);
      doc.roundedRect(chartX, barY, actualBarWidth, barHeight, barRadius, barRadius, 'F');
      
      // Draw affiliated portion (blue) on the left
      if (affiliatedWidth > 0 && affiliatedPct > 0) {
        doc.setFillColor(...affiliatedBarColor);
        if (affiliatedPct >= 100) {
          doc.roundedRect(chartX, barY, actualBarWidth, barHeight, barRadius, barRadius, 'F');
        } else {
          // Draw left portion with left rounding only
          doc.roundedRect(chartX, barY, affiliatedWidth + barRadius, barHeight, barRadius, barRadius, 'F');
          // Square off the right edge
          doc.rect(chartX + affiliatedWidth, barY, barRadius, barHeight, 'F');
        }
      }
    }
    
    currentY += rowHeight;
  };

  // Draw headers
  drawHeaders();
  
  // Draw each row
  const rowsToRender = data.slice(0, maxRows);
  rowsToRender.forEach((rowData, index) => {
    drawRow(rowData, index);
  });

  return currentY + 10;
};

/**
 * Draws the Shared Affiliation by Service Lines chart in table format
 */
export const drawSharedAffiliationByServiceLineChart = (doc, data, options = {}) => {
  const {
    x = null,
    y = 80,
    width = null,
    maxRows = 200,
    allData = null,  // Optional: pass all data for consistent scaling across pages
    showLegend = true,
    pdfType = 'printable',
  } = options;

  const settings = getSettingsByType(pdfType);
  const pageWidth = doc.internal.pageSize.width;
  
  // Table dimensions
  const startX = x || 20;
  const tableWidth = width || pageWidth - 40;
  
  // Row settings
  const rowHeight = pdfType === 'slideshow' ? 30 : 26;
  const headerHeight = pdfType === 'slideshow' ? 24 : 20;
  const fontSize = pdfType === 'slideshow' ? 10 : 8;
  const headerFontSize = pdfType === 'slideshow' ? 8 : 6;
  const nameFontSize = pdfType === 'slideshow' ? 11 : 9;
  
  // Colors
  const affiliatedBarColor = AFFILIATION_COLORS.affiliated;
  const nonAffiliatedBarColor = AFFILIATION_COLORS.nonAffiliated;
  const headerBgColor = [235, 241, 248];
  const headerTextColor = [100, 116, 139];
  const rowBgColor = [255, 255, 255];
  const altRowBgColor = [243, 247, 251];
  
  let currentY = y;
  
  // Find max value for scaling bars - use allData if provided
  const dataArray = Array.isArray(data) ? data : [];
  const dataForMax = allData || dataArray;
  const maxValue = dataForMax.reduce((max, row) => {
    const total = parseInt(row.shared_patients ?? row.total ?? 0, 10) || 0;
    return Math.max(max, total);
  }, 100);

  // Column layout for Service Lines
  // Service Line Name | Patients | % Affiliated | Affiliation Bar
  const nameW = 0.40;
  const patientsW = 0.12;
  const pctAffiliatedW = 0.15;
  const chartW = 0.33;
  
  const columns = {
    name:         { start: 0,                                    width: nameW },
    patients:     { start: nameW,                                width: patientsW },
    pctAffiliated:{ start: nameW + patientsW,                    width: pctAffiliatedW },
    chart:        { start: nameW + patientsW + pctAffiliatedW,   width: chartW },
  };

  // === Draw column headers ===
  const drawHeaders = () => {
    doc.setFillColor(...headerBgColor);
    doc.roundedRect(startX, currentY, tableWidth, headerHeight, 4, 4, 'F');
    doc.rect(startX, currentY + headerHeight / 2, tableWidth, headerHeight / 2, 'F');
    
    doc.setFontSize(headerFontSize);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(...headerTextColor);
    
    const headerY = currentY + headerHeight / 2 + 3;
    const headers = [
      { text: 'SERVICE LINE', col: 'name' },
      { text: 'PATIENTS', col: 'patients' },
      { text: '% AFFILIATED', col: 'pctAffiliated' },
      { text: 'PATIENTS', col: 'chart' },
    ];
    
    headers.forEach(header => {
      const colX = startX + columns[header.col].start * tableWidth + 5;
      const colWidth = columns[header.col].width * tableWidth - 10;
      const headerText = truncateText(doc, header.text, colWidth);
      doc.text(headerText, colX, headerY);
    });
    
    currentY += headerHeight + 2;
  };

  // === Draw a single row ===
  const drawRow = (rowData, index) => {
    const totalShared = parseInt(rowData.shared_patients ?? rowData.total ?? 0, 10) || 0;
    const rawAffiliated = rowData.shared_patients_affiliated ?? rowData.affiliated_totals;
    const affiliatedShared = (rawAffiliated === null || rawAffiliated === undefined) 
      ? 0 
      : (parseInt(rawAffiliated, 10) || 0);
    
    const affiliatedPct = totalShared > 0 ? (affiliatedShared / totalShared) * 100 : 0;
    
    const rawServiceLine = rowData.name || rowData.serviceline_name || rowData.service_line_name || rowData.label || `Service Line ${index + 1}`;
    const serviceLineName = toTitleCase(rawServiceLine);
    
    // Row background
    const bgColor = index % 2 === 0 ? rowBgColor : altRowBgColor;
    doc.setFillColor(...bgColor);
    doc.rect(startX, currentY, tableWidth, rowHeight, 'F');
    
    const textY = currentY + rowHeight / 2 + 3;
    const iconSize = pdfType === 'slideshow' ? 12 : 10;
    
    // === Service Line Name column ===
    const nameX = startX + columns.name.start * tableWidth + 5;
    doc.setFontSize(nameFontSize);
    doc.setFont('Roboto', 'normal');
    doc.setTextColor(33, 37, 41);
    const nameColWidth = columns.name.width * tableWidth - 10;
    const nameToDraw = truncateText(doc, serviceLineName, nameColWidth);
    doc.text(nameToDraw, nameX, textY);
    
    // === Patients column ===
    const patientsX = startX + columns.patients.start * tableWidth + 2;
    drawPersonIcon(doc, patientsX, currentY + (rowHeight - iconSize) / 2, iconSize, [156, 163, 175]);
    doc.setFontSize(fontSize);
    doc.setFont('RobotoLight', 'normal');
    doc.setTextColor(75, 85, 99);
    const countText = totalShared > 0 ? totalShared.toLocaleString() : '<11';
    doc.text(countText, patientsX + iconSize + 2, textY);
    
    // === % Affiliated column (right aligned with padding) ===
    const pctColWidth = columns.pctAffiliated.width * tableWidth;
    const pctX = startX + columns.pctAffiliated.start * tableWidth + pctColWidth - 10;
    doc.setFontSize(fontSize);
    doc.setFont('RobotoLight', 'normal');
    doc.setTextColor(75, 85, 99);
    const pctText = `${affiliatedPct.toFixed(2)}%`;
    doc.text(pctText, pctX, textY, { align: 'right' });
    
    // === Chart column (stacked bar) ===
    const chartX = startX + columns.chart.start * tableWidth + 5;
    const barWidth = columns.chart.width * tableWidth - 10;
    const barHeight = pdfType === 'slideshow' ? 20 : 18;  // Taller bars
    const barY = currentY + (rowHeight - barHeight) / 2;
    const barRadius = 2;  // Subtle rounded corners
    
    // Scale bar to max value
    const scale = maxValue > 0 ? (totalShared / maxValue) : 0;
    const scaledBarWidth = barWidth * scale;
    
    if (scaledBarWidth > 0) {
      const actualBarWidth = Math.max(scaledBarWidth, barRadius * 2);
      const affiliatedWidth = (affiliatedPct / 100) * actualBarWidth;
      
      // Draw full bar with non-affiliated color (rounded)
      doc.setFillColor(...nonAffiliatedBarColor);
      doc.roundedRect(chartX, barY, actualBarWidth, barHeight, barRadius, barRadius, 'F');
      
      // Draw affiliated portion (blue) on the left
      if (affiliatedWidth > 0 && affiliatedPct > 0) {
        doc.setFillColor(...affiliatedBarColor);
        if (affiliatedPct >= 100) {
          doc.roundedRect(chartX, barY, actualBarWidth, barHeight, barRadius, barRadius, 'F');
        } else {
          // Draw left portion with left rounding only
          doc.roundedRect(chartX, barY, affiliatedWidth + barRadius, barHeight, barRadius, barRadius, 'F');
          // Square off the right edge
          doc.rect(chartX + affiliatedWidth, barY, barRadius, barHeight, 'F');
        }
      }
    }
    
    currentY += rowHeight;
  };

  // Draw headers
  drawHeaders();
  
  // Draw each row
  const rowsToRender = dataArray.slice(0, maxRows);
  rowsToRender.forEach((rowData, index) => {
    drawRow(rowData, index);
  });

  return currentY + 10;
};

/**
 * Draws the Payors stacked horizontal bar chart
 */
export const drawPayorChart = (doc, data, payors = [], options = {}) => {
  const {
    x = null,
    y = 80,
    width = null,
    maxRows = 200,
    allData = null,  // Optional: pass all data for consistent scaling across pages
    showLegend = true,
    claimsType = 'providers',
    pdfType = 'printable',
    filters = {},
  } = options;

  const settings = getSettingsByType(pdfType);
  const pageWidth = doc.internal.pageSize.width;
  
  // Table dimensions
  const startX = x || 20;
  const tableWidth = width || pageWidth - 40;
  
  // Row settings
  const rowHeight = pdfType === 'slideshow' ? 30 : 26;
  const headerHeight = pdfType === 'slideshow' ? 24 : 20;
  const fontSize = pdfType === 'slideshow' ? 10 : 8;
  const headerFontSize = pdfType === 'slideshow' ? 8 : 6;
  const nameFontSize = pdfType === 'slideshow' ? 11 : 9;
  
  // Colors
  const headerBgColor = [235, 241, 248];
  const headerTextColor = [100, 116, 139];
  const rowBgColor = [255, 255, 255];
  const altRowBgColor = [243, 247, 251];
  const affiliatedBarColor = AFFILIATION_COLORS.affiliated;
  
  let currentY = y;
  
  const isOrganizations = claimsType === 'organizations';
  
  // Check if single specialty is filtered (hide specialty column if so)
  const hasSingleSpecialty = (
    (filters.specialty && filters.specialty !== 'All Specialties' && filters.specialty !== 'All') ||
    (filters.taxonomies && Array.isArray(filters.taxonomies) && filters.taxonomies.length === 1)
  );
  const showSpecialtyColumn = !hasSingleSpecialty;
  
  // Get payor colors
  const payorColors = payors.map((payor, idx) => {
    if (payor.color) {
      const rgb = colorToRgb(payor.color);
      if (rgb) return rgb;
    }
    return DEFAULT_PAYOR_COLORS[idx] || DEFAULT_PAYOR_COLORS[3];
  });

  // Column layout for Payors
  // For providers: Icon | Provider | Specialty (optional) | Organization | Payor Mix Bar
  // For organizations: Icon | Organization | Specialty (optional) | Payor Mix Bar
  const iconW = 0.03;
  const chartW = 0.36;  // Bigger bars (absorbed claims column width)
  
  let namePct, specialtyPct, orgPct;
  if (isOrganizations) {
    if (showSpecialtyColumn) {
      namePct = 0.49;
      specialtyPct = 0.12;  // Reduced - fits "Equipment Supplies"
    } else {
      namePct = 0.61;
      specialtyPct = 0;
    }
    orgPct = 0;
  } else {
    if (showSpecialtyColumn) {
      namePct = 0.24;
      specialtyPct = 0.12;  // Reduced - fits "Equipment Supplies"
      orgPct = 0.25;
    } else {
      namePct = 0.28;
      specialtyPct = 0;
      orgPct = 0.33;
    }
  }
  
  let columns;
  if (isOrganizations) {
    if (showSpecialtyColumn) {
      columns = {
        icon:      { start: 0,                                          width: iconW },
        name:      { start: iconW,                                      width: namePct },
        specialty: { start: iconW + namePct,                            width: specialtyPct },
        chart:     { start: iconW + namePct + specialtyPct,             width: chartW },
      };
    } else {
      columns = {
        icon:      { start: 0,                                          width: iconW },
        name:      { start: iconW,                                      width: namePct },
        chart:     { start: iconW + namePct,                            width: chartW },
      };
    }
  } else {
    if (showSpecialtyColumn) {
      columns = {
        icon:         { start: 0,                                                    width: iconW },
        name:         { start: iconW,                                                width: namePct },
        specialty:    { start: iconW + namePct,                                      width: specialtyPct },
        organization: { start: iconW + namePct + specialtyPct,                       width: orgPct },
        chart:        { start: iconW + namePct + specialtyPct + orgPct,              width: chartW },
      };
    } else {
      columns = {
        icon:         { start: 0,                                                    width: iconW },
        name:         { start: iconW,                                                width: namePct },
        organization: { start: iconW + namePct,                                      width: orgPct },
        chart:        { start: iconW + namePct + orgPct,                             width: chartW },
      };
    }
  }

  // === Draw column headers ===
  const drawHeaders = () => {
    doc.setFillColor(...headerBgColor);
    doc.roundedRect(startX, currentY, tableWidth, headerHeight, 4, 4, 'F');
    doc.rect(startX, currentY + headerHeight / 2, tableWidth, headerHeight / 2, 'F');
    
    doc.setFontSize(headerFontSize);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(...headerTextColor);
    
    const headerY = currentY + headerHeight / 2 + 3;
    let headers;
    if (isOrganizations) {
      headers = [
        { text: 'ORGANIZATION', col: 'name' },
        ...(showSpecialtyColumn ? [{ text: 'FACILITY TYPE', col: 'specialty' }] : []),
        { text: 'PAYOR MIX', col: 'chart' },
      ];
    } else {
      headers = [
        { text: 'PROVIDER', col: 'name' },
        ...(showSpecialtyColumn ? [{ text: 'SPECIALTY', col: 'specialty' }] : []),
        { text: 'ORGANIZATION', col: 'organization' },
        { text: 'PAYOR MIX', col: 'chart' },
      ];
    }
    
    headers.forEach(header => {
      const colX = startX + columns[header.col].start * tableWidth + 5;
      const colWidth = columns[header.col].width * tableWidth - 10;
      const headerText = truncateText(doc, header.text, colWidth);
      doc.text(headerText, colX, headerY);
    });
    
    currentY += headerHeight + 2;
  };

  // === Draw legend below headers ===
  const drawPayorLegendRow = () => {
    if (!showLegend || payors.length === 0) return;
    
    const legendHeight = pdfType === 'slideshow' ? 20 : 16;
    const legendY = currentY;
    const legendFontSize = pdfType === 'slideshow' ? 8 : 6;
    const circleRadius = pdfType === 'slideshow' ? 5 : 4;  // Circle radius instead of box size
    
    doc.setFontSize(legendFontSize);
    doc.setFont('Roboto', 'normal');
    
    let legendX = startX + 5;
    
    // Draw each payor legend item with circles
    payors.forEach((payor, idx) => {
      if (idx >= 3) return; // Max 3 payors
      
      const color = payorColors[idx];
      doc.setFillColor(...color);
      // Draw circle: x, y is center
      const circleY = legendY + legendHeight / 2;
      doc.circle(legendX + circleRadius, circleY, circleRadius, 'F');
      
      doc.setTextColor(60, 60, 60);
      const label = payor.name || `Payor ${idx + 1}`;
      const truncatedLabel = label.length > 20 ? label.substring(0, 18) + '...' : label;
      doc.text(truncatedLabel, legendX + circleRadius * 2 + 4, legendY + legendHeight / 2 + 2);
      
      legendX += circleRadius * 2 + doc.getTextWidth(truncatedLabel) + 15;
    });
    
    // Add "Other" legend item with circle
    doc.setFillColor(...DEFAULT_PAYOR_COLORS[3]);
    const circleY = legendY + legendHeight / 2;
    doc.circle(legendX + circleRadius, circleY, circleRadius, 'F');
    doc.setTextColor(60, 60, 60);
    doc.text('Other', legendX + circleRadius * 2 + 4, legendY + legendHeight / 2 + 2);
    
    currentY += legendHeight;  // Reduced spacing after legend
  };

  // === Draw a single row ===
  const drawRow = (claim, index) => {
    // Get display values
    let displayName = '';
    let isAffiliated = false;
    let orgName = '';
    let specialty = '';
    
    if (isOrganizations) {
      const org = claim.organization || claim.dataValues?.organization || {};
      const rawName = org.name || org.dataValues?.name || claim.name || 'Unknown Organization';
      displayName = rawName.toUpperCase();  // ALL CAPS for org names
      isAffiliated = org.is_affiliated || org.affiliated?.length > 0;
      // Use facility_type for organizations - check nested org first since that's where it comes from
      const rawFacilityType = org.facility_type || org.dataValues?.facility_type ||
                          claim.facility_type || claim.dataValues?.facility_type || '';
      specialty = toTitleCase(rawFacilityType);
    } else {
      const firstName = claim.provider?.first_name || '';
      const lastName = claim.provider?.last_name || '';
      const rawName = `${lastName}, ${firstName}`.trim().replace(/^,\s*/, '').replace(/,\s*$/, '') || 'Unknown Provider';
      displayName = rawName.toUpperCase();  // ALL CAPS for provider names
      isAffiliated = claim.provider?.is_affiliated || claim.provider?.clients?.length > 0;
      const rawOrg = claim.organization_name || claim.provider?.location?.organization?.name || '';
      orgName = toTitleCase(rawOrg);
      // Build taxonomy object in expected format for SpecialtyUtil
      const taxonomy = {
        doctivity_name: claim.specialty_doctivity_name,
        specialization: claim.specialty_specialization,
        classification: claim.specialty_classification
      };
      specialty = SpecialtyUtil.getNameFromTaxonomy(taxonomy);
    }
    
    const totalClaims = claim.total || claim.total_claims || 0;
    
    // Row background
    const bgColor = index % 2 === 0 ? rowBgColor : altRowBgColor;
    doc.setFillColor(...bgColor);
    doc.rect(startX, currentY, tableWidth, rowHeight, 'F');
    
    const textY = currentY + rowHeight / 2 + 3;
    const iconSize = pdfType === 'slideshow' ? 12 : 10;
    
    // === Icon column ===
    const iconX = startX + columns.icon.start * tableWidth + 5;
    if (isAffiliated) {
      drawAffiliatedHubIcon(doc, iconX, currentY + (rowHeight - iconSize) / 2, iconSize, affiliatedBarColor);
    }
    
    // === Name column ===
    const nameX = startX + columns.name.start * tableWidth + 5;
    doc.setFontSize(nameFontSize);
    doc.setFont('Roboto', 'normal');
    doc.setTextColor(33, 37, 41);
    const nameColWidth = columns.name.width * tableWidth - 10;
    const nameToDraw = truncateText(doc, displayName, nameColWidth);
    doc.text(nameToDraw, nameX, textY);
    
    // === Specialty column (conditional) ===
    if (showSpecialtyColumn && columns.specialty) {
      const specialtyX = startX + columns.specialty.start * tableWidth + 5;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      const specColWidth = columns.specialty.width * tableWidth - 10;
      const specToDraw = truncateText(doc, specialty || '-', specColWidth);
      doc.text(specToDraw, specialtyX, textY);
    }
    
    // === Organization column (only for providers) ===
    if (!isOrganizations && columns.organization) {
      const orgX = startX + columns.organization.start * tableWidth + 5;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      const orgColWidth = columns.organization.width * tableWidth - 10;
      const orgToDraw = truncateText(doc, orgName || '-', orgColWidth);
      doc.text(orgToDraw, orgX, textY);
    }
    
    // === Chart column (stacked payor bar with labels) ===
    const chartX = startX + columns.chart.start * tableWidth + 5;
    const barWidth = columns.chart.width * tableWidth - 10;
    const barHeight = pdfType === 'slideshow' ? 20 : 18;  // Taller bars
    const barY = currentY + (rowHeight - barHeight) / 2;
    const barRadius = 2;  // Subtle rounded corners
    
    // Calculate payor percentages and collect segment data
    let totalPct = 0;
    const segmentData = [];
    
    for (let i = 0; i < payors.length && i < 3; i++) {
      const pct = parseFloat(claim[`percent_${i}`]) || 0;
      totalPct += pct;
      if (pct > 0) {
        segmentData.push({ pct, color: payorColors[i] });
      }
    }
    
    // "Other" segment
    const otherPct = Math.max(0, 100 - totalPct);
    if (otherPct > 0) {
      segmentData.push({ pct: otherPct, color: DEFAULT_PAYOR_COLORS[3] });
    }
    
    // Draw rounded bar with segments
    const segments = [];
    if (segmentData.length > 0) {
      // Draw full bar with last segment's color (rounded)
      const lastColor = segmentData[segmentData.length - 1].color;
      doc.setFillColor(...lastColor);
      doc.roundedRect(chartX, barY, barWidth, barHeight, barRadius, barRadius, 'F');
      
      // Draw segments from left to right
      let currentBarX = chartX;
      let remainingWidth = barWidth;
      
      for (let i = 0; i < segmentData.length - 1; i++) {
        const seg = segmentData[i];
        const segmentWidth = (seg.pct / 100) * barWidth;
        
        if (segmentWidth > 0) {
          doc.setFillColor(...seg.color);
          if (i === 0) {
            // First segment - rounded on left only
            doc.roundedRect(currentBarX, barY, segmentWidth + barRadius, barHeight, barRadius, barRadius, 'F');
            doc.rect(currentBarX + segmentWidth, barY, barRadius, barHeight, 'F');
          } else {
            // Middle segments - no rounding
            doc.rect(currentBarX, barY, segmentWidth, barHeight, 'F');
          }
          segments.push({ x: currentBarX, width: segmentWidth, pct: seg.pct });
          currentBarX += segmentWidth;
          remainingWidth -= segmentWidth;
        }
      }
      
      // Last segment already drawn as background
      if (segmentData.length > 0) {
        segments.push({ x: currentBarX, width: remainingWidth, pct: segmentData[segmentData.length - 1].pct });
      }
    }
    
    // Draw percentage labels on segments (if enough space)
    // Center vertically: barY + barHeight/2 gives center, then adjust for text baseline
    const labelFontSize = pdfType === 'slideshow' ? 9 : 7;  // Larger font
    const barTextY = barY + (barHeight / 2) + (labelFontSize / 3);  // Better vertical centering
    doc.setFontSize(labelFontSize);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(255, 255, 255);
    
    segments.forEach(seg => {
      if (seg.width > 25 && seg.pct >= 5) {
        const label = `${Math.round(seg.pct)}%`;
        doc.text(label, seg.x + seg.width / 2, barTextY, { align: 'center' });
      }
    });
    
    currentY += rowHeight;
  };

  // Draw headers
  drawHeaders();
  
  // Draw each row
  const rowsToRender = data.slice(0, maxRows);
  rowsToRender.forEach((claim, index) => {
    drawRow(claim, index);
  });
  
  // Draw legend row at bottom (after all data rows)
  drawPayorLegendRow();

  return currentY + 10;
};

/**
 * Helper to convert various color formats to RGB array
 */
const colorToRgb = (color) => {
  if (!color) return null;
  
  if (Array.isArray(color)) {
    return color;
  }
  
  if (typeof color === 'string') {
    if (color.startsWith('#')) {
      return hexToRgb(color);
    }
    
    const rgbMatch = color.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (rgbMatch) {
      return [parseInt(rgbMatch[1], 10), parseInt(rgbMatch[2], 10), parseInt(rgbMatch[3], 10)];
    }
  }
  
  return null;
};

/**
 * Helper to convert hex color to RGB array
 */
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    return [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)];
  }
  const shortResult = /^#?([a-f\d])([a-f\d])([a-f\d])$/i.exec(hex);
  if (shortResult) {
    return [
      parseInt(shortResult[1] + shortResult[1], 16),
      parseInt(shortResult[2] + shortResult[2], 16),
      parseInt(shortResult[3] + shortResult[3], 16),
    ];
  }
  return [128, 128, 128];
};

export default {
  drawProjectedPatientsChart,
  drawSharedAffiliationChart,
  drawSharedAffiliationByServiceLineChart,
  drawPayorChart,
  drawChartLegend,
  drawLocationTypeLegend,
  drawAffiliationLegend,
  drawPayorLegend,
  drawAffiliationSummary,
  drawPageHeader,
  drawPageFooterWithLegend,
  AFFILIATION_COLORS,
  DEFAULT_PAYOR_COLORS,
};